'use client'

import { useEffect, useState } from 'react'

export const MARKETING_CHAT_PLACEHOLDER_PREFIX = 'Ask Atrium to '

/** Suffixes only (prefix is added by the hook). */
export const DEFAULT_MARKETING_CHAT_PLACEHOLDER_SUGGESTIONS: readonly string[] = [
  'create a video campaign for…',
  'run my ads on Disney+ during family movies',
  'run my ads during episodes of The Office',
  'show my product to male sports fans with disposable income',
  'reach new parents shopping for baby gear',
  'compare my CTV results on Netflix vs. Hulu',
  'pause spend automatically if ROAS drops below my goal',
  'scale my top creative to lookalike audiences',
  'book premium sports inventory during playoff season',
  'retarget everyone who watched my full video ad',
]

const TYPE_MS = 38
const DELETE_MS = 26
const HOLD_MS = 4000

function suggestionsKey(suggestions: readonly string[]) {
  return suggestions.join('\u0001')
}

/**
 * Full placeholder string for the marketing chat composer: static prefix plus a
 * suffix that types in, holds, then deletes before the next idea.
 */
export function useMarketingAnimatedPlaceholder(
  enabled: boolean,
  suggestions: readonly string[] = DEFAULT_MARKETING_CHAT_PLACEHOLDER_SUGGESTIONS
): string {
  const [placeholder, setPlaceholder] = useState(MARKETING_CHAT_PLACEHOLDER_PREFIX)

  useEffect(() => {
    if (!enabled || suggestions.length === 0) {
      setPlaceholder(
        MARKETING_CHAT_PLACEHOLDER_PREFIX + (suggestions[0] ?? '')
      )
      return
    }

    let cancelled = false
    let suggestionIndex = 0
    let suffixLen = 0
    let phase: 'in' | 'hold' | 'out' = 'in'
    let timeoutId: ReturnType<typeof setTimeout>

    const clear = () => {
      if (timeoutId != null) clearTimeout(timeoutId)
    }

    const schedule = (fn: () => void, ms: number) => {
      clear()
      timeoutId = setTimeout(fn, ms)
    }

    const tick = () => {
      if (cancelled) return
      const target = suggestions[suggestionIndex]

      if (phase === 'in') {
        if (suffixLen < target.length) {
          suffixLen += 1
          setPlaceholder(MARKETING_CHAT_PLACEHOLDER_PREFIX + target.slice(0, suffixLen))
          schedule(tick, TYPE_MS)
        } else {
          phase = 'hold'
          schedule(() => {
            if (cancelled) return
            phase = 'out'
            tick()
          }, HOLD_MS)
        }
      } else if (phase === 'out') {
        if (suffixLen > 0) {
          suffixLen -= 1
          setPlaceholder(MARKETING_CHAT_PLACEHOLDER_PREFIX + target.slice(0, suffixLen))
          schedule(tick, DELETE_MS)
        } else {
          suggestionIndex = (suggestionIndex + 1) % suggestions.length
          phase = 'in'
          tick()
        }
      }
    }

    suffixLen = 0
    suggestionIndex = 0
    phase = 'in'
    setPlaceholder(MARKETING_CHAT_PLACEHOLDER_PREFIX)
    tick()

    return () => {
      cancelled = true
      clear()
    }
  }, [enabled, suggestionsKey(suggestions)])

  return placeholder
}
